import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import { doc, setDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';

const GOLD = '#d4af37';

interface FestivalSupportContent {
  title: string;
  subtitle: string;
  description: string;
  festivalsTitle: string;
  festival1Name: string;
  festival1Date: string;
  festival1Description: string;
  festival2Name: string;
  festival2Date: string;
  festival2Description: string;
  festival3Name: string;
  festival3Date: string;
  festival3Description: string;
  impactTitle: string;
  impact1: string;
  impact2: string;
  impact3: string;
  levelsTitle: string;
  level1Name: string;
  level1Amount: string;
  level2Name: string;
  level2Amount: string;
  level3Name: string;
  level3Amount: string;
}

export default function FestivalSupportManager() {
  const [content, setContent] = useState<FestivalSupportContent>({
    title: 'Festival Support - Celebrate Sacred Traditions',
    subtitle: 'Help us celebrate with grandeur and devotion',
    description: 'Temple festivals are joyous celebrations of faith and community. Your support ensures we can host grand festivities with proper rituals, decorations, and celebrations that bring the community together.',
    festivalsTitle: 'Upcoming Festivals',
    festival1Name: 'Shivarathri',
    festival1Date: 'February 2024',
    festival1Description: 'The night of Shiva celebration with grand pujas, musical performances, and community gatherings.',
    festival2Name: 'Navaratri',
    festival2Date: 'September 2024',
    festival2Description: 'Nine nights of celebration honoring the Divine Mother with elaborate ceremonies and cultural events.',
    festival3Name: 'Diwali',
    festival3Date: 'November 2024',
    festival3Description: 'Festival of lights celebrating the victory of light over darkness with temple illumination and rituals.',
    impactTitle: 'How Your Support Helps',
    impact1: '₹2000 sponsors ritual offerings and flowers',
    impact2: '₹5000 funds festival decorations and lighting',
    impact3: '₹10000 sponsors complete festival celebrations',
    levelsTitle: 'Donation Levels',
    level1Name: 'Ritual Supporter',
    level1Amount: '₹2000',
    level2Name: 'Festival Contributor',
    level2Amount: '₹5000',
    level3Name: 'Festival Sponsor',
    level3Amount: '₹10000'
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  useEffect(() => {
    const unsubscribe = loadContent();
    return () => unsubscribe();
  }, []);

  const loadContent = () => {
    try {
      console.log('🔄 FestivalSupportManager: Setting up real-time listener...');
      const docRef = doc(db, 'festival_support_content', 'main');
      setLoading(true);
      
      const unsubscribe = onSnapshot(docRef, (docSnap) => {
        try {
          if (docSnap.exists()) {
            const data = docSnap.data() as FestivalSupportContent;
            setContent(data);
            console.log('✅ Admin: Real-time content loaded from Firebase');
          } else {
            console.warn('⚠️ Admin: Document does not exist, using defaults');
          }
        } catch (error) {
          console.error('Error processing content:', error);
        } finally {
          setLoading(false);
        }
      }, (error) => {
        console.error('❌ Admin Error in onSnapshot:', error);
        setSnackbar({ open: true, message: 'Error loading content', severity: 'error' });
        setLoading(false);
      });
      
      return unsubscribe;
    } catch (error) {
      console.error('Error setting up listener:', error);
      setSnackbar({ open: true, message: 'Error loading content', severity: 'error' });
      setLoading(false);
      return () => {};
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      console.log('💾 Saving festival support content to Firebase...');
      const docRef = doc(db, 'festival_support_content', 'main');
      await setDoc(docRef, content);
      console.log('✅ Content saved successfully to festival_support_content/main');
      setSnackbar({ open: true, message: 'Content saved successfully!', severity: 'success' });
    } catch (error) {
      console.error('❌ Error saving content:', error);
      setSnackbar({ open: true, message: 'Error saving content', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field: keyof FestivalSupportContent, value: string) => {
    setContent(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress sx={{ color: GOLD }} />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
            Festival Support Manager
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Manage the festival support donation page content
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            startIcon={<RefreshIcon />}
            onClick={loadContent}
            disabled={loading}
            variant="outlined"
            sx={{ borderColor: GOLD, color: GOLD, '&:hover': { bgcolor: GOLD + '15' } }}
          >
            Refresh
          </Button>
          <Button
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            variant="contained"
            sx={{ bgcolor: GOLD, color: '#000', fontWeight: 700 }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Box>

      <Paper elevation={0} sx={{ p: 3, mb: 3, border: '2px solid ' + GOLD, borderRadius: 2, bgcolor: '#fafaf8' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: GOLD, mb: 2 }}>
          📄 Main Content
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField fullWidth label="Title" value={content.title} onChange={(e) => handleChange('title', e.target.value)} size="small" />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Subtitle" value={content.subtitle} onChange={(e) => handleChange('subtitle', e.target.value)} size="small" />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Description" value={content.description} onChange={(e) => handleChange('description', e.target.value)} multiline rows={3} />
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={0} sx={{ p: 3, mb: 3, border: '2px solid ' + GOLD, borderRadius: 2, bgcolor: '#fafaf8' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: GOLD, mb: 2 }}>
          🎉 Upcoming Festivals
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField fullWidth label="Section Title" value={content.festivalsTitle} onChange={(e) => handleChange('festivalsTitle', e.target.value)} size="small" />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Festival 1 Name" value={content.festival1Name} onChange={(e) => handleChange('festival1Name', e.target.value)} size="small" sx={{ mb: 1 }} />
            <TextField fullWidth label="Festival 1 Date" value={content.festival1Date} onChange={(e) => handleChange('festival1Date', e.target.value)} size="small" sx={{ mb: 1 }} />
            <TextField fullWidth label="Festival 1 Description" value={content.festival1Description} onChange={(e) => handleChange('festival1Description', e.target.value)} multiline rows={2} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Festival 2 Name" value={content.festival2Name} onChange={(e) => handleChange('festival2Name', e.target.value)} size="small" sx={{ mb: 1 }} />
            <TextField fullWidth label="Festival 2 Date" value={content.festival2Date} onChange={(e) => handleChange('festival2Date', e.target.value)} size="small" sx={{ mb: 1 }} />
            <TextField fullWidth label="Festival 2 Description" value={content.festival2Description} onChange={(e) => handleChange('festival2Description', e.target.value)} multiline rows={2} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Festival 3 Name" value={content.festival3Name} onChange={(e) => handleChange('festival3Name', e.target.value)} size="small" sx={{ mb: 1 }} />
            <TextField fullWidth label="Festival 3 Date" value={content.festival3Date} onChange={(e) => handleChange('festival3Date', e.target.value)} size="small" sx={{ mb: 1 }} />
            <TextField fullWidth label="Festival 3 Description" value={content.festival3Description} onChange={(e) => handleChange('festival3Description', e.target.value)} multiline rows={2} />
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={0} sx={{ p: 3, mb: 3, border: '2px solid ' + GOLD, borderRadius: 2, bgcolor: '#fafaf8' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: GOLD, mb: 2 }}>
          💡 Your Impact
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField fullWidth label="Section Title" value={content.impactTitle} onChange={(e) => handleChange('impactTitle', e.target.value)} size="small" />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Impact 1" value={content.impact1} onChange={(e) => handleChange('impact1', e.target.value)} size="small" sx={{ mb: 1 }} />
            <TextField fullWidth label="Impact 2" value={content.impact2} onChange={(e) => handleChange('impact2', e.target.value)} size="small" sx={{ mb: 1 }} />
            <TextField fullWidth label="Impact 3" value={content.impact3} onChange={(e) => handleChange('impact3', e.target.value)} size="small" />
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={0} sx={{ p: 3, border: '2px solid ' + GOLD, borderRadius: 2, bgcolor: '#fafaf8' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: GOLD, mb: 2 }}>
          💰 Donation Levels
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField fullWidth label="Section Title" value={content.levelsTitle} onChange={(e) => handleChange('levelsTitle', e.target.value)} size="small" />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Level 1 Name" value={content.level1Name} onChange={(e) => handleChange('level1Name', e.target.value)} size="small" sx={{ mb: 1 }} />
            <TextField fullWidth label="Level 1 Amount" value={content.level1Amount} onChange={(e) => handleChange('level1Amount', e.target.value)} size="small" sx={{ mb: 2 }} />
            <TextField fullWidth label="Level 2 Name" value={content.level2Name} onChange={(e) => handleChange('level2Name', e.target.value)} size="small" sx={{ mb: 1 }} />
            <TextField fullWidth label="Level 2 Amount" value={content.level2Amount} onChange={(e) => handleChange('level2Amount', e.target.value)} size="small" sx={{ mb: 2 }} />
            <TextField fullWidth label="Level 3 Name" value={content.level3Name} onChange={(e) => handleChange('level3Name', e.target.value)} size="small" sx={{ mb: 1 }} />
            <TextField fullWidth label="Level 3 Amount" value={content.level3Amount} onChange={(e) => handleChange('level3Amount', e.target.value)} size="small" />
          </Grid>
        </Grid>
      </Paper>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity as 'success' | 'error'} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
